/**
 * Video Component Definition
 *
 * A video embed component supporting YouTube, Vimeo, and direct video URLs.
 */

export const videoComponent = {
  type: 'video',
  label: 'Video',
  icon: 'Video',
  description: 'Embed video from YouTube, Vimeo, or direct URL',
  category: 'content',

  canHaveChildren: false,

  resolveStyles() {
    return {
      width: '100%',
      alignSelf: 'flex-start',
    };
  },

  defaultProps: {
    // Content
    url: '',
    videoType: 'youtube', // 'youtube', 'vimeo', 'direct'
    thumbnailUrl: '',
    showPlayButton: true,

    // Playback
    autoplay: false,
    muted: false,
    loop: false,
    controls: true,

    // Size (responsive format)
    aspectRatio: '16:9',
    width: { base: { value: 100, unit: '%' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Advanced
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'videoType',
      type: 'select',
      label: 'Video Source',
      category: 'Content',
      default: 'youtube',
      description: 'Select the video platform. The URL will be automatically converted to the correct embed format.',
      options: [
        { value: 'youtube', label: 'YouTube' },
        { value: 'vimeo', label: 'Vimeo' },
        { value: 'direct', label: 'Direct URL (MP4, WebM)' },
      ],
    },
    {
      key: 'url',
      type: 'text',
      label: 'Video URL',
      category: 'Content',
      placeholder: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      description: 'YouTube: paste the full watch URL (youtube.com/watch?v=...) or short URL (youtu.be/...). Vimeo: paste the video page URL (vimeo.com/123456789). Direct: paste a direct .mp4 or .webm file URL.',
    },
    {
      key: 'thumbnailUrl',
      type: 'mediaPicker',
      label: 'Custom Thumbnail',
      category: 'Content',
      acceptTypes: 'image',
      placeholder: 'https://...',
    },
    {
      key: 'showPlayButton',
      type: 'checkbox',
      label: 'Show Play Button Overlay',
      category: 'Content',
      default: true,
    },

    // Playback category
    {
      key: 'autoplay',
      type: 'checkbox',
      label: 'Autoplay',
      category: 'Playback',
      default: false,
      description: 'Note: Autoplay may require muted audio',
    },
    {
      key: 'muted',
      type: 'checkbox',
      label: 'Muted',
      category: 'Playback',
      default: false,
    },
    {
      key: 'loop',
      type: 'checkbox',
      label: 'Loop',
      category: 'Playback',
      default: false,
    },
    {
      key: 'controls',
      type: 'checkbox',
      label: 'Show Controls',
      category: 'Playback',
      default: true,
    },

    // Size category
    {
      key: 'aspectRatio',
      type: 'select',
      label: 'Aspect Ratio',
      category: 'Size',
      default: '16:9',
      options: [
        { value: '16:9', label: '16:9 (Widescreen)' },
        { value: '4:3', label: '4:3 (Standard)' },
        { value: '1:1', label: '1:1 (Square)' },
        { value: '21:9', label: '21:9 (Cinematic)' },
        { value: '9:16', label: '9:16 (Vertical)' },
      ],
    },
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 50,
      default: 0,
    },

    // Advanced category
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default videoComponent;
